import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { contactItems, companyItems, IconLine } from "@/components/site/ContactInfo";
import { useLang, scrollToId } from "@/i18n";
import { CONTACT } from "@/content";
import { useConsent, setConsent } from "@/consent";

const Card = ({ title, children, className = "" }) => (
  <div className={`border border-white/10 bg-[#0D0D10] p-6 sm:p-8 ${className}`}>
    <p className="font-mono text-xs uppercase tracking-[0.25em] text-red-500">{title}</p>
    <div className="mt-6">{children}</div>
  </div>
);

const BigLink = ({ item }) => (
  <a href={item.href} data-testid={`contact-page-${item.id}`} target={item.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
    className="group flex items-center justify-between gap-4 py-4 border-b border-white/10 last:border-0">
    <span className="flex items-center gap-4 min-w-0">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-red-700/50 bg-red-950/40"><item.icon className="h-5 w-5 text-red-500" strokeWidth={1.5} /></span>
      <span className={`min-w-0 break-words text-white transition-colors group-hover:text-red-400 ${item.kind === "address" ? "text-base" : "font-display font-bold text-2xl sm:text-3xl tracking-wide"}`}>{item.value}</span>
    </span>
    <ArrowRight className="h-4 w-4 shrink-0 text-zinc-500 transition-transform group-hover:translate-x-1 group-hover:text-red-500" />
  </a>
);

export default function Contact() {
  const { t } = useLang();
  const c = t.contactPage;
  const navigate = useNavigate();
  const items = contactItems();
  const consent = useConsent();
  const book = () => { navigate("/"); setTimeout(() => scrollToId("booking"), 400); };
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="contact-page">
      <Nav />
      <main className="relative pt-36 pb-28 overflow-hidden">
        <div className="absolute inset-0 tac-grid" />
        <div className="absolute -top-40 -left-40 h-[560px] w-[560px] rounded-full bg-red-800/20 blur-[140px]" />
        <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow>{c.eyebrow}</Eyebrow>
            <h1 className="mt-6 font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.92] max-w-4xl" data-testid="contact-page-title">{c.title}</h1>
            <p className="mt-6 max-w-2xl text-zinc-300 text-base md:text-lg">{c.sub}</p>
            <button onClick={book} data-testid="contact-page-book-button" className="group mt-8 clip-btn inline-flex items-center gap-3 bg-red-600 hover:bg-red-500 transition-colors px-7 py-4 font-mono text-xs sm:text-sm uppercase tracking-[0.18em]">{c.book}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></button>
          </Reveal>
          <div className="mt-16 grid lg:grid-cols-12 gap-6">
            <Reveal className="lg:col-span-7 min-w-0 flex flex-col gap-6">
              <Card title={c.phones}>{items.filter((i) => i.kind === "phone").map((i) => <BigLink key={i.id} item={i} />)}</Card>
              <Card title={c.emails}>{items.filter((i) => i.kind === "email").map((i) => <BigLink key={i.id} item={i} />)}</Card>
              <Card title={c.address}>{items.filter((i) => i.kind === "address").map((i) => <BigLink key={i.id} item={i} />)}</Card>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-5 min-w-0 flex flex-col gap-6">
              <Card title={c.company} className="!bg-[linear-gradient(135deg,#2a0707_0%,#0D0D10_70%)] border-red-700/40">
                <div className="space-y-4" data-testid="contact-page-company">
                  {companyItems(t.footer).map((i) => (
                    <IconLine key={i.id} icon={i.icon} testId={`contact-page-${i.id}`}>
                      <span className="block font-mono text-[11px] uppercase tracking-widest text-zinc-400">{i.label}</span>
                      <span className="block text-white">{i.value}</span>
                    </IconLine>
                  ))}
                </div>
              </Card>
              <Card title={c.map}>
                <div className="relative aspect-[4/3] overflow-hidden border border-white/10">
                  {consent === "all" ? (
                    <iframe title="Armat Defense map" data-testid="contact-page-map" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapsQuery)}&z=15&output=embed`}
                      className="absolute inset-0 h-full w-full [filter:grayscale(1)_invert(0.92)_contrast(0.9)]" />
                  ) : (
                    <div data-testid="contact-page-map-blocked" className="absolute inset-0 tac-grid flex flex-col items-center justify-center gap-4 p-6 text-center">
                      <p className="text-sm text-zinc-300 max-w-xs">{t.cookie.mapBlocked}</p>
                      <button data-testid="contact-page-map-accept" onClick={() => setConsent("all")} className="clip-btn bg-red-600 hover:bg-red-500 transition-colors px-5 py-3 font-mono text-xs uppercase tracking-[0.16em]">{t.cookie.mapLoad}</button>
                    </div>
                  )}
                </div>
              </Card>
            </Reveal>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
